import { pgTable, text, serial, integer, boolean, timestamp, jsonb } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// User roles
export const userRoles = ["client", "caseWorker", "admin", "employer"] as const;
export type UserRole = typeof userRoles[number];

// User table
export const users = pgTable("users", {
  id: serial("id").primaryKey(),
  username: text("username").notNull().unique(),
  password: text("password").notNull(),
  email: text("email").notNull(),
  role: text("role").$type<UserRole>().notNull(),
  fullName: text("full_name").notNull(),
  phone: text("phone"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Client profiles
export const clientProfiles = pgTable("client_profiles", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => users.id),
  caseWorkerId: integer("case_worker_id").references(() => users.id),
  employmentGoal: text("employment_goal"),
  currentStatus: text("current_status").default("In Progress"),
  intakeDate: timestamp("intake_date").defaultNow().notNull(),
  resumeUrl: text("resume_url"),
  hasGED: boolean("has_ged").default(false),
  housingStatus: text("housing_status"),
  barriersToEmployment: text("barriers_to_employment").array(),
  notes: text("notes"),
});

// Employers
export const employers = pgTable("employers", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => users.id),
  companyName: text("company_name").notNull(),
  industry: text("industry"),
  companySize: text("company_size"),
  website: text("website"),
  description: text("description"),
  isVerified: boolean("is_verified").default(false),
});

// Job listings
export const jobs = pgTable("jobs", {
  id: serial("id").primaryKey(),
  employerId: integer("employer_id").notNull().references(() => employers.id),
  title: text("title").notNull(),
  description: text("description").notNull(),
  location: text("location").notNull(),
  salary: text("salary"),
  requirements: text("requirements"),
  isFilled: boolean("is_filled").default(false),
  postedDate: timestamp("posted_date").defaultNow().notNull(),
});

// Applications
export const applications = pgTable("applications", {
  id: serial("id").primaryKey(),
  jobId: integer("job_id").notNull().references(() => jobs.id),
  clientId: integer("client_id").notNull().references(() => clientProfiles.id),
  status: text("status").default("Pending"),
  appliedDate: timestamp("applied_date").defaultNow().notNull(),
  notes: text("notes"),
});

// Milestones
export const milestones = pgTable("milestones", {
  id: serial("id").primaryKey(),
  clientId: integer("client_id").notNull().references(() => clientProfiles.id),
  name: text("name").notNull(),
  isCompleted: boolean("is_completed").default(false),
  completedDate: timestamp("completed_date"),
  description: text("description"),
});

// Appointments
export const appointments = pgTable("appointments", {
  id: serial("id").primaryKey(),
  clientId: integer("client_id").notNull().references(() => clientProfiles.id),
  caseWorkerId: integer("case_worker_id").notNull().references(() => users.id),
  title: text("title").notNull(),
  date: timestamp("date").notNull(),
  notes: text("notes"),
  isCompleted: boolean("is_completed").default(false),
});

// Success Stories
export const successStories = pgTable("success_stories", {
  id: serial("id").primaryKey(),
  title: text("title").notNull(),
  clientName: text("client_name").notNull(),
  content: text("content").notNull(),
  imageUrl: text("image_url"),
  category: text("category"),
  publishDate: timestamp("publish_date").defaultNow().notNull(),
  isPublished: boolean("is_published").default(true),
});

// Contact form submissions
export const contactSubmissions = pgTable("contact_submissions", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  email: text("email").notNull(),
  phone: text("phone"),
  subject: text("subject").notNull(),
  message: text("message").notNull(),
  submittedAt: timestamp("submitted_at").defaultNow().notNull(),
  isResolved: boolean("is_resolved").default(false),
});

// Zod schemas for inserts
export const insertUserSchema = createInsertSchema(users).omit({ id: true, createdAt: true });
export const insertClientProfileSchema = createInsertSchema(clientProfiles).omit({ id: true });
export const insertEmployerSchema = createInsertSchema(employers).omit({ id: true });
export const insertJobSchema = createInsertSchema(jobs).omit({ id: true, postedDate: true });
export const insertApplicationSchema = createInsertSchema(applications).omit({ id: true, appliedDate: true });
export const insertMilestoneSchema = createInsertSchema(milestones).omit({ id: true, completedDate: true });
export const insertAppointmentSchema = createInsertSchema(appointments).omit({ id: true });
export const insertSuccessStorySchema = createInsertSchema(successStories).omit({ id: true, publishDate: true });
export const insertContactSubmissionSchema = createInsertSchema(contactSubmissions).omit({ id: true, submittedAt: true, isResolved: true });

// Types for inserts
export type InsertUser = z.infer<typeof insertUserSchema>;
export type InsertClientProfile = z.infer<typeof insertClientProfileSchema>;
export type InsertEmployer = z.infer<typeof insertEmployerSchema>;
export type InsertJob = z.infer<typeof insertJobSchema>;
export type InsertApplication = z.infer<typeof insertApplicationSchema>;
export type InsertMilestone = z.infer<typeof insertMilestoneSchema>;
export type InsertAppointment = z.infer<typeof insertAppointmentSchema>;
export type InsertSuccessStory = z.infer<typeof insertSuccessStorySchema>;
export type InsertContactSubmission = z.infer<typeof insertContactSubmissionSchema>;

// Types for selects
export type User = typeof users.$inferSelect;
export type ClientProfile = typeof clientProfiles.$inferSelect;
export type Employer = typeof employers.$inferSelect;
export type Job = typeof jobs.$inferSelect;
export type Application = typeof applications.$inferSelect;
export type Milestone = typeof milestones.$inferSelect;
export type Appointment = typeof appointments.$inferSelect;
export type SuccessStory = typeof successStories.$inferSelect;
export type ContactSubmission = typeof contactSubmissions.$inferSelect;

// Login & registration schemas
export const loginSchema = z.object({
  username: z.string().min(3, "Username must be at least 3 characters"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

export const registrationSchema = insertUserSchema.extend({
  password: z.string().min(6, "Password must be at least 6 characters"),
  confirmPassword: z.string(),
}).refine(data => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
});

export type LoginData = z.infer<typeof loginSchema>;
export type RegistrationData = z.infer<typeof registrationSchema>;

// City Economic Impact Simulation
export const cityData = pgTable("city_data", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  state: text("state").notNull(),
  population: integer("population").notNull(),
  annualReleases: integer("annual_releases").notNull(),
  baselineRecidivismRate: integer("baseline_recidivism_rate").notNull(), // percentage
  averageCostPerIncarceration: integer("average_cost_per_incarceration").notNull(), // annual cost
  currentEmploymentRate: integer("current_employment_rate").notNull(), // percentage
  medianIncome: integer("median_income").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const simulationResults = pgTable("simulation_results", {
  id: serial("id").primaryKey(),
  cityId: integer("city_id").notNull().references(() => cityData.id),
  employmentRate: integer("employment_rate").notNull(), // percentage
  supportType: text("support_type").notNull(), // "none", "training", "housing", "mentoring", "full_wraparound"
  timeframe: integer("timeframe").notNull(), // years
  projectedRecidivismReduction: integer("projected_recidivism_reduction").notNull(), // percentage
  publicCostSavings: integer("public_cost_savings").notNull(), // total dollars
  jobsCreated: integer("jobs_created").notNull(),
  roiPerDollar: integer("roi_per_dollar").notNull(), // cents per dollar invested
  taxpayerContribution: integer("taxpayer_contribution").notNull(), // annual contribution from employed individuals
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Insert schemas
export const insertCityDataSchema = createInsertSchema(cityData).omit({
  id: true,
  createdAt: true,
});

export const insertSimulationResultsSchema = createInsertSchema(simulationResults).omit({
  id: true,
  createdAt: true,
});

// Types
export type CityData = typeof cityData.$inferSelect;
export type InsertCityData = z.infer<typeof insertCityDataSchema>;
export type SimulationResults = typeof simulationResults.$inferSelect;
export type InsertSimulationResults = z.infer<typeof insertSimulationResultsSchema>;

// Simulation parameters schema
export const simulationParametersSchema = z.object({
  cityId: z.number(),
  participantCount: z.number().min(100).max(10000),
  supportType: z.enum(["none", "training", "housing", "mentoring", "full_wraparound"]),
  timeframe: z.number().min(1).max(10),
});

export type SimulationParameters = z.infer<typeof simulationParametersSchema>;
